/*******************************************************************************
* Licensed Materials - Property of HCL 
* (c) Copyright HCL Technologies Ltd. 2020. All Rights Reserved. 

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

/**
 * Component that create a BarWidget with labels
 * required params:
 *  - data: object with data matrix and labels
 *  Example : {
        values: [
            {
                values: [10, 8],
                labels: ["Waiting", "Ready", "Held"],
                colors: ["#008C98", "#C9D6F2", "#C9D6F2"],
            },
            {
                values: [10],
                labels: ["Running"],
                colors: ["#6C87C3"],
            },
            {
                values: [10],
                labels: ["Successful"],
                colors: ["#00A95D"],
            },
            {
                values: [10, 8, 3],
                labels: ["Error", "Blocked"],
                colors: ["#f15d4d", "#6c87c3"],
            },
            {
                values: [8],
                labels: ["Suppressed", "Canceled", "Undecided"],
                colors: ["#7B7373", "#C9D6F2", "#C9D6F2"],
            },
        ],
        labels: ["Not Running", "Running", "Successful", "With issues", "Suppressed"],
    }
 */

import React from "react";
import PropTypes from "prop-types";
import Text from "../GUI/Text";

function BarChartPanel({
    aframeClasses,
    data,
    aspectRatio = 1.777,
    grouped,
    fontColor = "#c6c6c6",
    flat = false,
    setSelected,
    ...otherAttributes
}) {
    if (!data) return null;
    const selected = data.selected || data.values[0];

    const drawPrimary = () => {
        return data.values.map((v, i) => {
            const height = v.values.reduce((a, b) => a + b, 0);

            return (
                <a-entity key={i} position={`${i * scaleFactor.x} 0 0`}>
                    {height && (
                        <a-box
                            depth={
                                flat ? scaleFactor.x / 20 : scaleFactor.x / 2
                            }
                            width={scaleFactor.x / 2}
                            height={height * scaleFactor.y}
                            color={v.colors[0]}
                            position={`0 ${(height * scaleFactor.y) / 2} 0`}
                        />
                    )}
                    {grouped && (
                        <a-plane
                            class={aframeClasses}
                            width={scaleFactor.x}
                            height={maxYValue * scaleFactor.y + 1}
                            position={`0 ${
                                (-height / 2 + maxYValue / 2) * scaleFactor.y -
                                0.5 +
                                (height * scaleFactor.y) / 2
                            } ${flat ? 0 : -0.5}`}
                            color="#363636"
                            opacity={selected.labels[0] === v.labels[0] ? 1 : 0}
                            animation__mouseenter_color={`property: opacity; startEvents:mouseenter; to: 1; dur:150; enabled: ${
                                selected.labels[0] !== v.labels[0]
                            }`}
                            animation__mouseleave_color={`property: opacity; startEvents:mouseleave; to: 0; dur:150; enabled: ${
                                selected.labels[0] !== v.labels[0]
                            }`}
                            onClick={(e) => {
                                setSelected(v);
                            }}
                        />
                    )}
                </a-entity>
            );
        });
    };

    const drawSecondary = () => {
        const maxvalue = Math.max(...selected.values);
        const columns = selected.values.length;
        const scale = {
            x: (secondaryPanelSpace * 0.8) / columns,
            y: (xMax * 0.7) / maxvalue
        };
        if (!selected.labels.length)
            return (
                <Text
                    text="No data available"
                    width={xMax}
                    color={fontColor}
                    sizeDecimal={50 / xMax}
                    align="center"
                    position={`0 0 ${flat ? 0.001 : scale.x / 4}`}
                />
            );

        return selected.values.map((v, i) => {
            return (
                <a-entity key={i} position={`0 ${i * -scale.x} 0.001`}>
                    <Text
                        position={`${-xMax / 2 + xMax * 0.01} 0 ${
                            flat ? 0 : scale.x / 4
                        }`}
                        value={selected.labels[i]}
                        width={xMax * 0.2}
                        color={fontColor}
                        sizeDecimal={50 / xMax}
                    />
                    {v && (
                        <a-entity
                            key={i}
                            rotation="0 0 -90"
                            position={`${-xMax / 2 + xMax * 0.2} 0 0`}
                        >
                            <a-box
                                width={scale.x / 2}
                                depth={flat ? scale.x / 20 : scale.x / 2}
                                height={v * scale.y}
                                color={selected.colors[i]}
                                position={`0 ${(v * scale.y) / 2} 0`}
                            />
                        </a-entity>
                    )}
                    <Text
                        position={`${xMax / 2 - xMax * 0.01} 0 0`}
                        text={v.toString()}
                        width={xMax * 0.1}
                        align="right"
                        color={fontColor}
                        sizeDecimal={50 / xMax}
                    />
                </a-entity>
            );
        });
    };

    // Takes labels and return an array of Text ready to be renderized by React
    const createLabels = () => {
        const { labels } = data;
        const xMaxLen = Math.max.apply(
            Math,
            labels.map((e) => {
                return e.length;
            })
        );

        return labels.map((value, index) => {
            return (
                <a-entity key={value + index}>
                    <Text
                        position={`${index * scaleFactor.x} ${
                            scaleFactor.x * -0.4
                        } 0`}
                        text={
                            grouped
                                ? value +
                                  "\n" +
                                  data.values[index].values.reduce(
                                      (a, b) => a + b,
                                      0
                                  )
                                : value
                        }
                        width={scaleFactor.x}
                        align="center"
                        color={fontColor}
                        sizeDecimal={xMaxLen / scaleFactor.x}
                    />
                    {!grouped && (
                        <Text
                            position={`${index * scaleFactor.x} ${
                                data.values[index].values[0] * scaleFactor.y +
                                scaleFactor.x * 0.4
                            } 0`}
                            text={data.values[index].values[0]}
                            width={scaleFactor.x}
                            align="center"
                            color={fontColor}
                            sizeDecimal={xMaxLen / scaleFactor.x}
                        />
                    )}
                </a-entity>
            );
        });
    };

    const drawAxis = () => {
        const lines = {};
        lines.line__horizontal = `start: ${-xMax / 2} ${-yMax / 2} 0.01; end: ${
            xMax / 2
        } ${-yMax / 2} 0.01; color: ${fontColor}`;
        lines.line__vertical = `start: ${-xMax / 2} ${-yMax / 2} 0.01; end: ${
            -xMax / 2
        } ${yMax / 2} 0.01; color: ${fontColor}`;

        const numberTicks = 5;
        const tickHeight = yMax / numberTicks;
        for (let i = 0; i <= numberTicks; i++)
            lines[`line__${i}`] = `start: ${-xMax / 2 - scaleFactor.x / 8} ${
                -yMax / 2 + i * tickHeight
            } 0.01; end:${-xMax / 2 + scaleFactor.x / 8} ${
                -yMax / 2 + i * tickHeight
            } 0.01; color: ${fontColor}`;
        return <a-entity {...lines} />;
    };

    const maxYValue = Math.max(
        ...data.values
            .map((v) => {
                return v.values.reduce((a, b) => a + b, 0);
            })
            .flat(1)
    );
    let xMax = data.values.length;
    let yMax = maxYValue;

    if (yMax > xMax) yMax = xMax / aspectRatio;
    else xMax = yMax * aspectRatio;

    const secondaryPanelSpace = yMax / 4;
    if (grouped) yMax -= secondaryPanelSpace + 1;
    const scaleFactor = {
        x: xMax / data.values.length,
        y: yMax / maxYValue
    };

    return (
        <a-entity class="barPanelWidget" {...otherAttributes}>
            <a-entity
                position={`${-xMax / 2 + scaleFactor.x / 2} ${yMax / -2} ${
                    flat ? 0 : scaleFactor.x / 4
                }`}
            >
                {drawPrimary()}
                {data.labels && createLabels()}
            </a-entity>
            {drawAxis()}
            {grouped && (
                <a-plane
                    width={xMax}
                    height={secondaryPanelSpace}
                    position={`0 ${-yMax / 2 - secondaryPanelSpace / 2 - 0.6} ${
                        flat ? 0 : -scaleFactor.x / 4 + 0.01
                    }`}
                    color="#363636"
                >
                    <a-entity
                        position={`0 ${
                            selected.values.length - 1
                                ? (secondaryPanelSpace * 0.8) / 2 -
                                  secondaryPanelSpace /
                                      selected.values.length /
                                      2
                                : 0
                        } 0`}
                    >
                        {drawSecondary()}
                    </a-entity>
                </a-plane>
            )}
        </a-entity>
    );
}

// requiring data
BarChartPanel.propTypes = {
    aspectRatio: PropTypes.number
};

export default BarChartPanel;
